<template>
  <CarouselCard class="insider-carousel-card" :class="getCardClass">
    <div v-if="isNew" class="insider-carousel-card__new-flag">NEW</div>
    <div
      v-if="isAnnouncement"
      class="insider-carousel-card__body"
      @click="$emit('product-selected', product)"
    >
      <div class="insider-carousel-card__body-content">
        <h3
          v-if="product.title"
          class="insider-carousel-card__announcement-title"
          :class="isNew ? 'new' : ''"
        >
          {{ product.title }}
        </h3>
        <p class="insider-carousel-card__announcement-publish-date">
          {{ getDisplayDate }}
        </p>
        <p
          class="insider-carousel-card__description"
          v-html="product.trimmed_description"
        />
        <p class="insider-carousel-card__announcement-link">read article</p>
      </div>
    </div>
    <div v-else>
      <div
        class="insider-carousel-card__image-container"
        :class="getCardClass"
        @click="
          isAcademy ? openAttachment() : $emit('product-selected', product)
        "
        @mouseover="isHoveringLock = true"
        @mouseleave="isHoveringLock = false"
      >
        <div
          v-if="isExecutiveSeriesWebinar && !product.purchased"
          class="insider-carousel-card__lock-overlay"
        >
          <IconLock :width="100" :height="100" :unlock="isHoveringLock" />
        </div>
        <div
          :class="
            showPlayButton
              ? 'insider-carousel-card__play-button-overlay insider-carousel-card__play-button-overlay-visible'
              : 'insider-carousel-card__play-button-overlay insider-carousel-card__play-button-overlay-hidden'
          "
        >
          <img
            src="/images/play-icon.png"
            alt="Play Button"
            class="insider-carousel-card__play-button-overlay-icon"
          />
        </div>
        <div
          :class="
            showPopOut
              ? 'insider-carousel-card__play-button-overlay insider-carousel-card__play-button-overlay-visible'
              : 'insider-carousel-card__play-button-overlay insider-carousel-card__play-button-overlay-hidden'
          "
        >
          <img
            src="/images/pop-out.png"
            alt="Pop Out Button"
            class="insider-carousel-card__play-button-overlay-icon"
          />
        </div>
        <img
          v-if="product.image"
          :src="product.image"
          :alt="product.title"
          class="insider-carousel-card__image"
        />
        <div v-if="isNew" class="insider-carousel-card__new-flag">NEW</div>
      </div>
      <div class="insider-carousel-card__body">
        <div class="insider-carousel-card__body-content">
          <div v-if="isAcademy" class="academy">
            <div>
              <h3
                class="insider-carousel-card__title"
                @click="openAttachment()"
              >
                {{ product.title }}
              </h3>
            </div>
            <div>
              <BaseButton
                v-if="product.button && isAcademy"
                @click="openAttachment()"
                class="insider-carousel-card__academy-button"
              >
                {{ product.button }}
              </BaseButton>
            </div>
          </div>
          <div v-else>
            <h3
              v-if="product.title"
              :class="
                isArticle
                  ? 'insider-carousel-card__article-title'
                  : 'insider-carousel-card__title'
              "
              @click="$emit('product-selected', product)"
            >
              {{ product.title }}
            </h3>
            <p
              v-if="!isAcademy"
              class="insider-carousel-card__publish-date"
              @click="$emit('product-selected', product)"
            >
              {{ getDisplayDate }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </CarouselCard>
</template>

<script>
  import CarouselCard from '~/components/carousel/CarouselCard.vue';
  import IconLock from '~/components/icon/IconLock.vue';
  import IconPlayButton from '~/components/icon/IconPlayButton.vue';
  import BaseButton from '~/components/base/BaseButton.vue';

  /*  PRODUCT OBJECT
   *  id
   *  title
   *  short_description
   *  url
   *  vimeo_id
   *  youtube_id
   *  image
   *  publish_date
   *  purchased
   *  button
   *  attachment
   */
  export default {
    name: 'insider-carousel-card',
    components: {
      CarouselCard,
      IconLock,
      BaseButton,
      IconPlayButton,
    },
    props: {
      product: {
        type: Object,
        required: true,
      },
      productType: {
        type: String,
        default: 'product',
      },
    },
    data: () => ({
      isHoveringLock: false,
    }),
    computed: {
      isNew() {
        const parsedTimeStamp = new Date(this.product.publish_date);
        const currentDate = new Date();
        if (parsedTimeStamp >= currentDate) return false;
        const timeDifference = currentDate - parsedTimeStamp;
        const sevenDaysInMilliseconds = 8 * 24 * 60 * 60 * 1000;
        return timeDifference <= sevenDaysInMilliseconds;
      },
      isComingSoon() {
        if (!this.product.publish_date) {
          return true;
        }
        let parsedTimeStamp = new Date(this.product.publish_date);
        const currentDate = new Date();
        return parsedTimeStamp > currentDate;
      },
      showPlayButton() {
        if (this.isBusinessSeriesWebinar || this.isExecutiveSeriesWebinar) {
          return (
            this.isHoveringLock &&
            this.product.vimeo_id &&
            this.product.purchased
          );
        }
        if (this.isTrendsTalk) {
          return (
            this.isHoveringLock &&
            (this.product.vimeo_id || this.product.youtube_id)
          );
        }
      },
      showPopOut() {
        return this.isHoveringLock && (this.isArticle || this.isAcademy);
      },
      isAcademy() {
        return this.productType.includes('academy');
      },
      isAnnouncement() {
        return this.productType.includes('announcement');
      },
      isBusinessSeriesWebinar() {
        return this.productType === 'business-series-webinar';
      },
      isExecutiveSeriesWebinar() {
        return this.productType === 'executive-series-webinar';
      },
      isTrendsTalk() {
        return this.productType.includes('trends-talk');
      },
      isArticle() {
        return this.productType.includes('article');
      },
      showFullDate() {
        return this.isTrendsTalk || this.isArticle;
      },
      getDisplayDate() {
        let parsedTimeStamp = new Date(this.product.publish_date);
        if (this.isAnnouncement) {
          return parsedTimeStamp.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        }
        if (this.showFullDate) {
          let displayDate = parsedTimeStamp.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          return `Published on ${displayDate}`;
        }
        if (
          this.isExecutiveSeriesWebinar &&
          this.product.purchased &&
          !this.product.vimeo_id
        ) {
          return 'Coming Soon';
        }
        return parsedTimeStamp.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
        });
      },
      getCardClass() {
        if (this.isBusinessSeriesWebinar || this.isExecutiveSeriesWebinar) {
          return 'webinar';
        }
        if (this.isArticle) {
          return 'article';
        }
        if (this.isTrendsTalk) {
          return 'trendstalk';
        }
        if (this.isAcademy) {
          return 'academy';
        }
      },
    },
    methods: {
      openAttachment() {
        // window.open(`/storage/${attachment}`, '_blank')
        if (this.product.attachment) {
          return window.open(this.product.attachment, '_blank');
        }
        return window.open(this.product.url, '_blank');
      },
      getFilename(attachment) {
        // Remove the path from the filename
        return attachment.split('/').pop();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .insider-carousel-card {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;

    &__image-container.webinar {
      position: relative;
      height: 147px;
      background: linear-gradient(
        145deg,
        var(--itr-grey-mid),
        var(--itr-grey),
        var(--itr-grey-soft),
        #f1f2f3
      );
      @media (min-width: 768px) {
        height: 183px;
      }
      @media (min-width: 1200px) {
        height: 266px;
      }
    }
    &__image-container.article {
      position: relative;
      height: 176px;
      background: linear-gradient(
        145deg,
        var(--itr-grey-mid),
        var(--itr-grey),
        var(--itr-grey-soft),
        #f1f2f3
      );
      @media (min-width: 768px) {
        height: 230px;
      }
      @media (min-width: 1200px) {
        height: 277px;
      }
    }
    &__image-container.trendstalk {
      position: relative;
      height: 125px;
      background: linear-gradient(
        145deg,
        var(--itr-grey-mid),
        var(--itr-grey),
        var(--itr-grey-soft),
        #f1f2f3
      );
      @media (min-width: 768px) {
        height: 156px;
      }
      @media (min-width: 1200px) {
        height: 226px;
      }
    }
    &__image-container.academy {
      position: relative;
      height: 122px;
      background: linear-gradient(
        145deg,
        var(--itr-grey-mid),
        var(--itr-grey),
        var(--itr-grey-soft),
        #f1f2f3
      );
      @media (min-width: 768px) {
        height: 152px;
      }
      @media (min-width: 1200px) {
        height: 222px;
      }
    }
    &__lock-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.4);
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &__play-button-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.4);
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &__play-button-overlay-visible {
      opacity: 1;
      transition: opacity 0.2s ease-in;
    }
    &__play-button-overlay-hidden {
      opacity: 0;
      transition: opacity 0.2s ease-out;
    }
    &__image {
      width: 100%;
      height: 100%;

      object-fit: cover;
      object-position: center;
      cursor: pointer;
    }
    &__title {
      font-size: 18px;
      font-weight: 900;
      color: var(--itr-blue-mid);
      margin-top: 1rem;
    }
    &__description {
      flex: 1 auto;
      font-size: 14px;
    }
    &__publish-date {
      font-size: 14px;
      font-weight: 500;
      color: var(--itr-grey-charcoal);
    }
    &__body {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 0;
      cursor: pointer;
      .academy {
        margin-top: 1rem;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
        @include screen-xl {
          flex-direction: row;
        }
        .insider-carousel-card__title {
          width: 100%;
          @include screen-xl {
            margin: 0;
          }
        }
        .insider-carousel-card__academy-button {
          line-height: 20px;
          padding: 12px 24px;
          &:hover {
            background-color: $green;
          }
        }
      }
    }
    &__body-content {
      flex: 1 auto;
    }
    &__button {
      font-size: 1rem;
      font-weight: bold;
      color: #fff;
      background-color: #007bff;
      border-color: #007bff;
      display: inline-block;
      padding: 0.5rem 1rem;
      margin-top: 1rem;
      margin-bottom: 1rem;
      border-radius: 0.25rem;
      transition:
        color 0.15s ease-in-out,
        background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
    }
    &__link {
      color: var(--itr-green-mid);
      font-size: 1rem;
      font-weight: 700;
    }
    &__new-flag {
      position: absolute;
      right: -20px;
      top: 20px;
      color: #fff;
      background-color: #65a9d2;
      padding: 2px 18px;
      font-weight: 700;
      border-top-left-radius: 0.25rem;
      border-bottom-left-radius: 0.25rem;
      font-size: 20px;
      box-shadow: -4px -4px 4px rgba(0, 0, 0, 0.25);
      &::after {
        position: absolute;
        top: -20px;
        right: 0;
        content: '';
        height: 20px;
        width: 20px;
        border-bottom: solid 10px #3075a4;
        border-left: solid 10px #3075a4;
        border-right: solid 10px transparent;
        border-top: solid 10px transparent;
      }
    }
    &__announcement-title {
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
    &__announcement-title.new {
      margin-right: 65px;
    }
    &__announcement-publish-date {
      font-size: 14px;
      text-transform: uppercase;
      font-weight: 700;
      color: var(--itr-grey-mid);
      cursor: pointer;
    }
    &__announcement-link {
      color: var(--itr-green-mid);
      font-size: 16px;
      font-weight: 700;
      text-transform: uppercase;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>
